/**
 * CardVariantPicker - compact variant selector shown on product cards.
 *
 * Renders option values (sizes as pills, colors as swatches) plus an add-to-cart
 * form. When a complete option combination is selected it:
 *   1. Resolves the matching variant from the embedded JSON data.
 *   2. Writes the variant ID into the hidden input of the add-to-cart form.
 *   3. Enables the "Agregar" button (or marks it "Agotado" when unavailable).
 *   4. Dispatches the standard product select event so the product card syncs
 *      its links and swatches (product-card.js handles it).
 */

class CardVariantPicker extends HTMLElement {
  connectedCallback() {
    if (this.dataset.hydrated === 'true') return;
    this.dataset.hydrated = 'true';

    const variantsScript = this.querySelector('script[type="application/json"][data-card-picker-variants]');
    if (!variantsScript) return;
    try {
      this.variants = JSON.parse(variantsScript.textContent || '[]');
    } catch {
      this.variants = [];
    }

    this.selected = {}; // optionPosition (string) -> value name
    this.autoValues = []; // [{ position, value }] pre-selected single-value options
    const autoValuesScript = this.querySelector('script[type="application/json"][data-card-picker-auto-values]');
    if (autoValuesScript) {
      try {
        this.autoValues = JSON.parse(autoValuesScript.textContent || '[]');
      } catch {
        this.autoValues = [];
      }
    }
    this.autoValues.forEach(({ position, value }) => {
      this.selected[String(position)] = value;
    });

    this.valueButtons = Array.from(this.querySelectorAll('[data-card-picker-value]'));
    this.variantIdInput = this.querySelector('[data-card-picker-variant-id]');
    this.addButton = this.querySelector('[data-card-picker-add]');
    this.addButtonText = this.querySelector('[data-card-picker-add-text]');
    this.optionCount = Number(this.dataset.optionCount || 0);
    this.productUrl = this.dataset.productUrl || '';
    this.productId = this.dataset.productId || '';
    this.productTitle = this.dataset.productTitle || '';

    this.valueButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleValue(button);
      });
    });

    // Keep the panel open while interacting with it (mobile).
    this.addEventListener('pointerenter', () => this.classList.add('is-open'));
    this.addEventListener('pointerleave', () => this.classList.remove('is-open'));

    this.resolveVariant();
  }

  /**
   * Single-select a value per option (clicking again deselects).
   * @param {HTMLButtonElement} button
   */
  toggleValue(button) {
    const option = button.dataset.cardPickerOption;
    const value = button.dataset.cardPickerValue;

    if (this.selected[option] === value) {
      delete this.selected[option];
    } else {
      this.selected[option] = value;
    }

    this.valueButtons.forEach((btn) => {
      if (btn.dataset.cardPickerOption === option) {
        btn.classList.toggle('is-selected', this.selected[option] === btn.dataset.cardPickerValue);
      }
    });

    this.resolveVariant();
  }

  /**
   * @returns {string[]} Selected values ordered by option position.
   */
  getSelectedValues() {
    return Object.keys(this.selected)
      .map(Number)
      .sort((a, b) => a - b)
      .map((position) => this.selected[position]);
  }

  resolveVariant() {
    if (!this.variantIdInput || !this.addButton || !this.addButtonText) return;

    const values = this.getSelectedValues();
    const complete = values.length === this.optionCount;
    const selectedButtons = this.valueButtons.filter((btn) => btn.classList.contains('is-selected'));

    // Find the exact variant matching the full combination, or a partial match
    // for the last selected value (used to grab its option value id).
    let variant = null;
    let optionValueId = '';
    if (values.length > 0) {
      variant = this.variants.find((v) => JSON.stringify(v.options) === JSON.stringify(values)) || null;
      const lastButton = selectedButtons[selectedButtons.length - 1];
      if (lastButton) optionValueId = lastButton.dataset.optionValueId || '';
    }

    if (!complete || !variant) {
      this.variantIdInput.value = '';
      this.addButton.disabled = true;
      this.addButtonText.textContent = 'Elegir';
      return;
    }

    this.variantIdInput.value = String(variant.id);
    this.addButton.disabled = !variant.available;
    const isCartContext = this.dataset.context === 'cart';
    this.addButtonText.textContent = !variant.available
      ? 'Agotado'
      : isCartContext
        ? '+ Agregar'
        : 'Agregar';

    // Sync the rest of the card (links, swatches) with the resolved variant.
    const selectEvent = new CustomEvent('shopify:product:select', {
      bubbles: true,
      composed: true,
      detail: {
        optionValueId,
        variantId: String(variant.id),
        connectedProductUrl: this.productUrl || '',
      },
    });
    // product-card.js awaits event.promise to re-render variant data; resolve
    // immediately since this picker doesn't perform a section re-fetch.
    selectEvent.promise = Promise.resolve({ detail: {} });
    this.dispatchEvent(selectEvent);
  }
}

if (!customElements.get('card-variant-picker')) {
  customElements.define('card-variant-picker', CardVariantPicker);
}
